import { AppUnity, Unity, unityDtoAsDomain } from "../../domain/unity";
import {
  DeleteUnityDto,
  GetUnityDto,
  PostUnityDto,
  PutUnityDto,
} from "../../network/unity/Unity";
import { FirestoreSimpleCrudSource } from "../source/FirestoreSimpleCrudSource";
import { UnityStoreDao } from "../database/dao/Unity";
import { isEmpty, isNil } from "lodash";
import { UserStoreDao } from "../database/dao/User";
import { where } from "firebase/firestore";
import { UserWrapper } from "../../domain/user/User";
import { DataWithReference } from "../source/FirestoreSource";
import { httpsCallable } from "firebase/functions";
import FirebaseFunctions from "../source/FirebaseFunctions";

export default class UnityRepository {
  static async searchByKeyword(keyword: string): Promise<Unity[]> {
    const all = await UnityStoreDao.getList();
    const term = keyword.toLowerCase();

    return all.filter(
      (u) =>
        u.label.toLowerCase().includes(term) ||
        String(u.id).toLowerCase().includes(term)
    );
  }
  static firestoreRepository = new FirestoreSimpleCrudSource<
    GetUnityDto,
    PostUnityDto,
    PutUnityDto
  >("unity");

  static async getList(
    forced: boolean = false,
    appUser?: UserWrapper
  ): Promise<Unity[] | undefined> {
    let localList = await UnityStoreDao.getList();
    if (isEmpty(localList) || forced || localList.length === 1) {
      await UnityStoreDao.clear();
      let remoteList: DataWithReference<GetUnityDto>[] | undefined;

      if (appUser?.isClient() || appUser?.isSupervisor()) {
        if (appUser!.user.clientUnity) {
          remoteList = await this.firestoreRepository.getList([
            where("id", "in", appUser!.user.clientUnity),
          ]);
        }
      } else {
        remoteList = await this.firestoreRepository.getList();
      }
      if (remoteList) {
        const list = remoteList.map((data) => unityDtoAsDomain(data.data));
        await UnityStoreDao.putUnity(...list);
      }
      localList = await UnityStoreDao.getList();
    }
    if (!!localList) {
      return Promise.resolve(
        Promise.all(
          localList.map(async (lUnity) => await this.injectUnity(lUnity))
        )
      );
    }
  }

  static async create(object: PostUnityDto) {
    const createFunction = httpsCallable(
      FirebaseFunctions.functions,
      "createUnity"
    );
    const dto = (await createFunction(object)).data as GetUnityDto;
    await UnityStoreDao.putUnity(unityDtoAsDomain(dto));
  }

  static async delete(object: DeleteUnityDto): Promise<void> {
    const deleteFunction = httpsCallable(
      FirebaseFunctions.functions,
      "deleteUnity"
    );

    const result = (await deleteFunction(object)).data as boolean;
    if (result) await UnityStoreDao.deleteById(object.id);
  }

  static async getUnity(unityId: number | string, refresh: boolean = false) {
    return await this.getUnityBy(unityId, refresh);
  }

  static async getUnityForSearch(label: string, refresh: boolean = false) {
    return await this.getUnityByLabel(label, refresh);
  }

  static async update(object: PutUnityDto) {
    const updateFunction = httpsCallable(
      FirebaseFunctions.functions,
      "editUnity"
    );
    const result = (await updateFunction(object)).data as PutUnityDto["data"];
    await UnityStoreDao.updateById(object.id, result);
  }

  private static async getUnityBy(
    by: number | string,
    refresh: boolean = false
  ): Promise<Unity | undefined> {
    let localUnity = await UnityStoreDao.getUnityById(by);
    if (!localUnity || refresh) {
      const firestoreUnity = await this.firestoreRepository.getByReference(
        `${this.firestoreRepository.path}/${by}`,
        refresh
      );
      if (!isNil(firestoreUnity.data)) {
        const unity = unityDtoAsDomain(firestoreUnity.data);
        await UnityStoreDao.putUnity(unity);
        return await this.injectUnity(unity);
      }
    } else {
      if (localUnity) {
        return await this.injectUnity(localUnity);
      }
    }
  }

  private static async getUnityByLabel(
    by: string,
    refresh: boolean = false
  ): Promise<Unity | Unity[] | undefined> {
    // console.log("by", by);
    let localVehicles = await UnityStoreDao.getList();

    if (!localVehicles || refresh) {
      const firestoreUnity = await this.firestoreRepository.getByReference(
        `${this.firestoreRepository.path}/${by}`,
        refresh
      );
      if (firestoreUnity) {
        const unity = unityDtoAsDomain(firestoreUnity.data);
        await UnityStoreDao.putUnity(unity);
        return await this.injectUnity(unity);
      }
    } else {
      if (localVehicles) {
        // return localVehicles;
        // return await this.injectUnity(localUnity);
      }
    }
  }

  private static async injectUnity(unity: Unity): Promise<Unity> {
    if (unity && unity.id) {
      const userCount = await UserStoreDao.getUserCountForUnity(unity.id);
      const clientList = await UserStoreDao.getClientsForUnity(unity.id);
      unity.operatorCount = userCount;
      unity.client = clientList;
    }
    return unity;
  }
}
